import React, { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Back, Bounce, gsap, Power2, Power4, TweenMax } from 'gsap';
import { useTranslation } from 'react-i18next';

import { useEmojiWasm } from '@pkg/hooks';
import { randomValueBetween, splitTextInTwo } from '@pkg/utils';

import { type EmojiId } from '@app/client/common/structs/emoji/emoji';
import config from '@app/config';

import { GenerationLogoOne } from '../GenerationLogo';
import '../Input/_Input.scss';
import YatFullSizeScrollContainer from '../Layout/FullSizeScrollContainer';

import { coordinates, multiplyYat, parallaxSpeeds as speed } from './utilities';

import {
    ActionSlate,
    AvoidFlickering,
    BackgroundWrapper,
    ConfirmationText,
    ContentHolder,
    type variant,
    Yat,
    YatBackdrop,
    YatEmoji,
    YatGeneration,
    YatLanding,
} from './styles';

interface props {
    yat: EmojiId;
    confirmationText: string;
    children?: ReactNode;
    textVariant?: 'primary' | 'compact';
    variant: variant;
}

export const ConfirmationScreen = ({ yat, confirmationText, children, textVariant = 'primary', variant }: props) => {
    const { t } = useTranslation(['common', 'translation']);
    const [yatCoordinates] = useState(coordinates);
    const [parallaxSpeeds] = useState(speed);
    const app = useRef(null);
    const confirmationTextRef = useRef<HTMLDivElement>(null);
    const yatBackdropRef = useRef<HTMLDivElement>(null);
    const actionSlateRef = useRef<HTMLDivElement>(null);
    const yatEmojiRef = useRef<HTMLDivElement>(null);
    const [displayEmoji, setDisplayEmoji] = useState<EmojiId>('');

    const { emojiWasm } = useEmojiWasm();

    // Load the wasm and validate the Yat
    useEffect(() => {
        if (yat && emojiWasm) {
            try {
                const emojiInstance = new emojiWasm.EmojiId(yat, config.maxLengthYat);
                if (emojiInstance) {
                    const displayYat = emojiInstance.splitCanonical();
                    setDisplayEmoji(displayYat);
                }
            } catch (e) {
                throw Error(e as string);
            }
        }
    }, [yat, emojiWasm]);

    const allYats = useMemo(() => multiplyYat(displayEmoji), [displayEmoji]);
    /**
     * Create a background parallax
     * use the windows width and height to determine
     * our mouse area for X and Y
     */
    const handleBackgroundParallax = useCallback(
        (e) => {
            /**
             * Create a slightly different timing/speed for background behaviour
             * add a cubic ease in for all elements at different speed
             */
            if (yatBackdropRef?.current) {
                const yatBackdrop = yatBackdropRef.current;

                [...(yatBackdrop?.children as HTMLCollectionOf<HTMLElement>)].forEach((eachYats, index) => {
                    const yatX = e.pageX - eachYats?.offsetLeft / 2;
                    const yatY = e.pageY - eachYats?.offsetTop / 2;
                    TweenMax.to(eachYats, 1, {
                        x: 1 - yatX * parallaxSpeeds[index],
                        y: 1 - yatY * parallaxSpeeds[index],
                        ease: Power2.easeOut,
                    });
                });
            }
        },

        [parallaxSpeeds],
    );

    useEffect(() => {
        if (!displayEmoji) return;
        /**
         * Create and initiate timeline
         * add a delay property to create a wait experience so the emoji zooms out
         */
        const tl = gsap.timeline({
            delay: 0.6,
        });
        // Avoid Flickering
        TweenMax.to(app.current, 0, { css: { visibility: 'visible' } });
        /**
         * Create a slide in up effect for all text
         * add a quint ease in for all elements
         */
        if (confirmationTextRef?.current) {
            tl.staggerFrom(
                [...confirmationTextRef.current.children].map((el) => el.children),
                1,
                {
                    y: 195,
                    opacity: 0.3,
                    ease: Power4.easeOut,
                },
                0.1,
            );
        }
        /**
         * Create a scale in animation for the yat background
         * add a blow in ease in for all yat emoji elements
         */
        const yatEmojis = yatEmojiRef.current;
        if (yatEmojis) {
            const rotation = variant === 'paymentSuccess' ? -15 : 0;

            tl.fromTo(
                yatEmojis,
                0.45,
                {
                    scale: 1.4,
                    rotate: 0,
                    opacity: 0,
                },
                {
                    opacity: 1,
                    scale: 1,
                    rotate: rotation,
                    ease: Bounce.easeOut,
                },
                0.7,
            );
        }
        /**
         * Create a fade in animation for the action text and button
         * add a quint ease in for all text elements
         */
        const actionText = actionSlateRef?.current ? actionSlateRef.current.children[0] : null;
        const actionButton = actionText ? actionText.nextSibling : null;
        tl.fromTo(
            actionText,
            1.2,
            { y: 10, opacity: 0, ease: Power4.easeOut },
            { y: 10, opacity: 1, ease: Power4.easeOut },
            1.1,
        ).fromTo(
            actionButton,
            2,
            { y: 10, opacity: 0, ease: Power4.easeOut },
            { y: 10, opacity: 1, ease: Power4.easeOut },
            1.4,
        );

        // /**
        //  * Create a scale in animation for the yat background
        //  * add a blow in ease in for all yat emoji elements
        //  */
        const yatBackdrop = yatBackdropRef.current;
        if (yatBackdrop) {
            tl.from(
                [...yatBackdrop.children],
                {
                    scaleX: 0,
                    scaleY: 0,
                    ease: Back.easeOut,
                    stagger: randomValueBetween({ min: 0.08, max: 0.15 }),
                    duration: randomValueBetween({ min: 0.4, max: 1.1 }),
                },
                0.8,
            );
        }
    }, [displayEmoji, variant]);

    useEffect(() => {
        const ref = yatBackdropRef.current;
        if (ref) document.addEventListener('mousemove', handleBackgroundParallax);
        return () => {
            document.removeEventListener('mousemove', handleBackgroundParallax);
        };
    }, [handleBackgroundParallax]);

    const textWrap = useMemo(() => {
        if (textVariant === 'compact') {
            const splitText = splitTextInTwo(confirmationText);
            return (
                <div className="text-wrap compact">
                    <h1>{splitText.s1}</h1>
                    <h1>{splitText.s2}</h1>
                </div>
            );
        }
        return confirmationText.split(' ').map((text, index) => (
            <div className="text-wrap" key={randomValueBetween({ min: 0, max: 1 }) * index}>
                <h1>{text}</h1>
            </div>
        ));
    }, [confirmationText, textVariant]);

    return (
        <YatFullSizeScrollContainer>
            <BackgroundWrapper>
                <AvoidFlickering ref={app}>
                    <YatBackdrop ref={yatBackdropRef}>
                        {allYats.map((displayEmoji, index) => (
                            <Yat
                                key={index}
                                style={{
                                    transformOrigin: 'center center',
                                    ...yatCoordinates[index],
                                }}
                                role="img"
                            >
                                {displayEmoji}
                            </Yat>
                        ))}
                    </YatBackdrop>
                    <ContentHolder>
                        {variant === 'paymentSuccess' && config.yatGen1Start < new Date() && (
                            <YatGeneration>
                                <GenerationLogoOne />
                            </YatGeneration>
                        )}

                        <YatLanding>
                            <YatEmoji ref={yatEmojiRef} $variant={variant}>
                                <div className="slate-backdrop" aria-hidden />
                                <h3
                                    aria-label={t('your_new_yat', { yat: t('translation:yat') })}
                                    role="img"
                                    data-testid="yat-emoji"
                                >
                                    {displayEmoji}
                                </h3>
                            </YatEmoji>
                            <ConfirmationText $variant={variant} ref={confirmationTextRef}>
                                {textWrap}
                            </ConfirmationText>
                        </YatLanding>

                        <ActionSlate $variant={variant} ref={actionSlateRef}>
                            {children}
                        </ActionSlate>
                    </ContentHolder>
                </AvoidFlickering>
            </BackgroundWrapper>
        </YatFullSizeScrollContainer>
    );
};
